import { RefObject, useRef } from 'react';
import {
  mergeProps,
  useFocusRing,
  useSlider,
  useSliderThumb,
  VisuallyHidden,
} from 'react-aria';
import { SliderState, useSliderState } from 'react-stately';

import clsxm from '@/lib/clsxm';

function parseTime(seconds: number): number[] {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  seconds = seconds - hours * 3600 - minutes * 60;
  return [hours, minutes, seconds];
}

function formatTime(
  seconds: number[],
  totalSeconds: number[] = seconds,
): string {
  const totalWithoutLeadingZeroes = totalSeconds.slice(
    totalSeconds.findIndex((x: number) => x !== 0),
  );
  return seconds
    .slice(seconds.length - totalWithoutLeadingZeroes.length)
    .map((x: number) => x.toString().padStart(2, '0'))
    .join(':');
}

interface ThumbProps {
  state: SliderState;
  trackRef: RefObject<HTMLDivElement | null>;
  focusProps: any;
  isFocusVisible: boolean;
  index: number;
  onChangeStart?: () => void;
}

function Thumb(props: ThumbProps) {
  const { state, trackRef, focusProps, isFocusVisible, index } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { thumbProps, inputProps } = useSliderThumb(
    {
      index,
      trackRef: trackRef as RefObject<HTMLDivElement>,
      inputRef: inputRef as RefObject<HTMLInputElement>,
    },
    state,
  );

  return (
    <div
      className='absolute top-2/3 -translate-x-2/3'
      style={{
        left: `${state.getThumbPercent(index) * 100}%`,
      }}
    >
      <div
        {...thumbProps}
        onMouseDown={(...args) => {
          if (thumbProps?.onMouseDown) {
            thumbProps.onMouseDown(...args);
          }
          if (props.onChangeStart) {
            props.onChangeStart();
          }
        }}
        onPointerDown={(...args) => {
          if (thumbProps?.onPointerDown) {
            thumbProps?.onPointerDown(...args);
          }
          props.onChangeStart?.();
        }}
        className={clsxm(
          'h-4 rounded-full',
          isFocusVisible || state.isThumbDragging(index)
            ? 'w-1.5 bg-slate-900'
            : 'w-1 bg-slate-700',
        )}
      >
        <VisuallyHidden>
          <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
        </VisuallyHidden>
      </div>
    </div>
  );
}

// interface SliderProps {
//   label?: string;
//   onChangeStart?: () => void;
// }

export function Slider(props: any) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const state = useSliderState(props);
  const { groupProps, trackProps, labelProps, outputProps } = useSlider(
    props,
    state,
    trackRef as RefObject<HTMLDivElement>,
  );
  const { focusProps, isFocusVisible } = useFocusRing();

  const currentTime = parseTime(state.getThumbValue(0));
  const totalTime = parseTime(state.getThumbMaxValue(0));

  return (
    <div
      {...groupProps}
      className='absolute inset-x-0 bottom-full flex flex-auto touch-none items-center gap-6 md:relative'
    >
      {props.label && (
        <label className='sr-only' {...labelProps}>
          {props.label}
        </label>
      )}
      <div
        {...trackProps}
        onMouseDown={(...args) => {
          if (trackProps?.onMouseDown) {
            trackProps?.onMouseDown(...args);
          }
          props.onChangeStart?.();
        }}
        onPointerDown={(...args) => {
          if (trackProps?.onPointerDown) {
            trackProps?.onPointerDown(...args);
          }
          props.onChangeStart?.();
        }}
        ref={trackRef}
        className='relative w-full bg-slate-100 md:rounded-full'
      >
        <div
          className={clsxm(
            'h-2 md:rounded-r-md md:rounded-l-xl',
            isFocusVisible || state.isThumbDragging(0)
              ? 'bg-slate-900'
              : 'bg-slate-700',
          )}
          style={{
            width:
              state.getThumbValue(0) === 0
                ? 0
                : `calc(${state.getThumbPercent(0) * 100}% - ${
                    isFocusVisible || state.isThumbDragging(0)
                      ? '0.3125rem'
                      : '0.25rem'
                  })`,
          }}
        />
        <Thumb
          index={0}
          state={state}
          trackRef={trackRef as RefObject<HTMLDivElement>}
          onChangeStart={props.onChangeStart}
          focusProps={focusProps}
          isFocusVisible={isFocusVisible}
        />
      </div>
      <div className='hidden items-center gap-2 md:flex'>
        <output
          {...outputProps}
          aria-live='off'
          className={clsxm(
            'hidden rounded-md px-1 py-0.5 font-mono text-sm leading-6 md:block',
            state.getThumbMaxValue(0) === 0 && 'opacity-0',
            isFocusVisible || state.isThumbDragging(0)
              ? 'bg-slate-100 text-slate-900'
              : 'text-slate-500',
          )}
        >
          {formatTime(currentTime, totalTime)}
        </output>
        <span className='text-sm leading-6 text-slate-300' aria-hidden='true'>
          /
        </span>
        <span
          className={clsxm(
            'hidden rounded-md px-1 py-0.5 font-mono text-sm leading-6 text-slate-500 md:block',
            state.getThumbMaxValue(0) === 0 && 'opacity-0',
          )}
        >
          {formatTime(totalTime)}
        </span>
      </div>
    </div>
  );
}
